/**
 * VariantSelectPage — 세부 버전 선택 (기획확정안 §3.3).
 *
 * 흐름: 기본 카드 식별 → **여기** → 미등급·등급 선택 → 시세 (카드 상세).
 *
 * 규칙:
 *  - 전체 버전 목록을 공통으로 보여주지 않는다. 식별된 카드에 **실제 발행된 버전만** 제시.
 *  - 버전이 하나뿐이면 이 단계를 생략한다 → 라우팅 단계에서 카드 상세로 replace.
 *  - "잘 모르겠어요" 선택 시 확대 이미지·기울이기/플래시 안내를 제공하고,
 *    끝까지 확정하지 못하면 기본 카드 정보까지만 보여주며 시세는 확정하지 않는다.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@astryxdesign/core/Card";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { VStack } from "@astryxdesign/core/Layout";
import { findCard, CARD_GRADIENTS } from "../../data/dummyCards";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import styles from "./VariantSelectPage.module.css";

/**
 * @param cardSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어 useParams
 *   가 비므로 resolver 가 URL 에서 파싱해 넘긴다. 라우트 렌더에서는 생략.
 */
export default function VariantSelectPage({
  cardSerialNo: serialNoProp,
}: { cardSerialNo?: string } = {}) {
  const { t } = useTranslation("card");
  const overlayNavigate = useOverlayNavigate();
  // ⚠ 라우트 파라미터는 cardSerialNo(숫자)로 바뀌었지만 이 화면은 아직 더미 카탈로그를
  //   본다 — 실 카드로 들어오면 찾지 못한다. 카드 상세와 같은 방식으로 실 API 전환 필요.
  const { cardSerialNo: paramSerialNo } = useParams<{ cardSerialNo: string }>();
  const cardSerialNo = serialNoProp ?? paramSerialNo;
  const card = cardSerialNo ? findCard(cardSerialNo) : undefined;
  const [selected, setSelected] = useState<string | null>(null);
  const [unsure, setUnsure] = useState(false);

  // §3.3 "버전이 하나뿐이면 선택 단계를 생략한다"
  const onlyOne = !!card && card.variants.length <= 1;
  useEffect(() => {
    if (!card) return;
    if (onlyOne) {
      overlayNavigate(`/cards/${card.cardUid}`, { replace: true });
    }
  }, [card, onlyOne, overlayNavigate]);

  if (!card) {
    return (
      <div className={styles.page}>
        <p className={styles.notFound}>{t("detail.notFound")}</p>
      </div>
    );
  }
  if (onlyOne) return null; // 위 effect 가 replace 하는 동안 빈 화면

  const goDetail = (varntCd: string | null) => {
    // varntCd=null → 미확정. 카드 상세가 시세를 "참고용"으로만 표시한다.
    overlayNavigate(`/cards/${card.cardUid}${varntCd ? `?varnt=${varntCd}` : "?varnt=UNCONFIRMED"}`);
  };

  return (
    <div className={styles.page}>
      <VStack gap={5}>
        <VStack gap={2}>
          <Heading level={3}>{t("variant.title")}</Heading>
          <Text type="supporting" color="secondary">
            {t("variant.subtitle", { name: card.cardNm, number: card.cardNo })}
          </Text>
          <Text type="supporting" color="secondary">{t("variant.guide")}</Text>
        </VStack>

        <ul className={styles.list}>
          {card.variants.map((v) => (
            <li key={v.varntCd}>
              <button
                type="button"
                className={`${styles.option} ${selected === v.varntCd ? styles.optionSelected : ""}`}
                aria-pressed={selected === v.varntCd}
                onClick={() => { setSelected(v.varntCd); setUnsure(false); }}
              >
                <span
                  className={styles.swatch}
                  style={{ background: CARD_GRADIENTS[card.colorKey] }}
                />
                <span className={styles.optionMain}>
                  <span className={styles.optionLabel}>{v.label}</span>
                  {/* 힌트는 "잘 모르겠어요" 를 눌렀을 때 구분 방법으로 강조된다 */}
                  {(unsure || selected === v.varntCd) && v.hint && (
                    <span className={styles.optionHint}>{v.hint}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <VStack gap={3}>
          <Button
            variant="primary"
            size="lg"
            label={t("variant.confirm")}
            isDisabled={!selected}
            clickAction={() => goDetail(selected)}
          />
          <button type="button" className={styles.unsureButton} onClick={() => setUnsure(true)}>
            {t("variant.unsure")}
          </button>
          {unsure && (
            <Card padding={4}>
              <VStack gap={3}>
                <Text type="supporting" color="secondary">{t("variant.unsureGuide")}</Text>
                <Button
                  variant="secondary"
                  label={t("variant.skipNotice")}
                  clickAction={() => goDetail(null)}
                />
              </VStack>
            </Card>
          )}
        </VStack>
      </VStack>
    </div>
  );
}
